/**
 * Reading helpers over the fetched tree: locating an article, walking the flat
 * reading order for previous/next, and flattening blocks to plain text for search.
 */

import type {
  HelpArticle,
  HelpBlock,
  HelpCategory,
  HelpContent,
  HelpGroup,
} from "./api/types"

export interface ArticleLocation {
  category: HelpCategory
  group: HelpGroup
  article: HelpArticle
}

/** Every article in reading order — the order the category pages present them. */
export function flattenArticles(content: HelpContent): ArticleLocation[] {
  return content.categories.flatMap((category) =>
    category.groups.flatMap((group) =>
      group.articles.map((article) => ({ category, group, article })),
    ),
  )
}

export function findArticle(
  content: HelpContent,
  articleId: string,
): ArticleLocation | null {
  return (
    flattenArticles(content).find((entry) => entry.article.id === articleId) ??
    null
  )
}

export function findCategory(
  content: HelpContent,
  categoryId: string,
): HelpCategory | null {
  return (
    content.categories.find((category) => category.id === categoryId) ?? null
  )
}

export function adjacentArticles(
  content: HelpContent,
  articleId: string,
): { previous: ArticleLocation | null; next: ArticleLocation | null } {
  const all = flattenArticles(content)
  const index = all.findIndex((entry) => entry.article.id === articleId)
  if (index === -1) return { previous: null, next: null }
  return {
    previous: index > 0 ? all[index - 1] : null,
    next: index < all.length - 1 ? all[index + 1] : null,
  }
}

export function featuredArticles(content: HelpContent): ArticleLocation[] {
  return flattenArticles(content).filter((entry) => entry.article.featured)
}

/** All prose in a block, screen references unwrapped so a search for "Stammdaten"
 * matches `{{Stammdaten}}`. */
export function blockText(block: HelpBlock): string {
  const parts = [block.heading, block.text, block.label, ...block.items]
  block.steps.forEach((step) => parts.push(step.title, step.text))
  block.issues.forEach((issue) => parts.push(issue.symptom, issue.resolution))
  return parts
    .filter((part): part is string => Boolean(part))
    .join(" ")
    .replace(/[{}]/g, "")
}

export function articleText(article: HelpArticle): string {
  return [
    article.title,
    article.description,
    ...article.blocks.map(blockText),
  ].join(" ")
}

/** Languages an article can be written in. German is authoritative. */
export const HELP_LANGS = ["de", "en"] as const

/**
 * The language the editor should open on for a page.
 *
 * The one it already has text in, preferring the reader's own language, then
 * German. Opening on the UI language regardless would show an empty editor for a
 * German-only page and quietly start an English version instead of editing it.
 */
export function editorLang(
  uiLang: string,
  written: { publishedLangs: string[]; draftLangs: string[] },
): string {
  const has = new Set([...written.publishedLangs, ...written.draftLangs])
  if (has.has(uiLang)) return uiLang
  if (has.has("de")) return "de"
  const [first] = [...has]
  return first ?? "de"
}
